// connect/nettiConnect.ts — "connect this" handed to netti's rail.
//
// Every place a connect press lands (the connections lists, the orbits, netti's
// own connect tool, "not in the list? ask netti") asks here first. netti's rail
// (components/netti/NettiDock, when F.nettiConnect is open to the person and the
// rail is drawn: not on a phone) hears the ask, opens, and walks the tool in the
// conversation (lib/connect/walk). It says so on the event itself (`taken`), so
// the caller knows at once whether to open its own sheet instead: nobody ever
// presses connect and gets nothing.
//
// The same pattern as askNetti (components/netti/nettiAsk): a synchronous event
// whose listener writes its answer onto the detail.

export const NETTI_CONNECT_EVENT = "innernet:netti-connect";

/** open one connection's own settings sheet ({ id }): the walk's "open its settings", heard by NettiUiBridge */
export const CONNECT_SHEET_EVENT = "innernet:connect-sheet";

/** a connect walk said its last line ({ tool, id }): the first-run walk takes it as the connection kept */
export const CONNECT_WALK_DONE_EVENT = "innernet:connect-walk-done";

/** on <html> while the first-run connect step is netti's (components/os/OsShell): the onboarding guide
 *  steps aside, the connections space opens on its tools with no invite, and a tool pressed there opens
 *  the rail's walk (John, 2026-10-28: "straight to the list … click one, netti guides me through it"). */
export const CONNECT_IN_NETTI_ATTR = "data-connect-in-netti";

/** on <html> while the shell stands in the connections space (components/os/OsShell). A connect conversation
 *  belongs there: walking out of it, or a guide taking the screen, folds a rail that holds one, so it never
 *  surfaces over something else (John, 2026-10-29: "those connection chats should only open up when you're
 *  on a tool connection area"). */
export const IN_CONNECTIONS_ATTR = "data-in-connections";

export interface NettiConnectAsk {
  /** a CONNECT_TOOLS id, or nothing to let netti ask what it is */
  tool?: string | null;
  /** take up this connection (a waiting one, pressed in a list) */
  connection?: string | null;
  /** the words the person typed when there is no tool yet ("raycast") */
  what?: string | null;
  /** start the walk on this step (netti's connect tool may ask for one) */
  step?: string | null;
  /** where the press came from, for the funnel */
  from: string;
  /** written by the rail: it took the ask */
  taken?: boolean;
}

/** is the rail hidden by something holding the screen? The onboarding guide (`data-netti-guide`) and the
 *  overture (`data-overture`) retire the corner and the rail (globals.css), so a walk started there runs
 *  where nobody can see it: a returning account stuck on the connect step pressed a tool and got nothing
 *  (John, 2026-10-28). While either is up the rail takes no press, and the caller opens its own sheet. */
function railRetired(): boolean {
  if (typeof document === "undefined") return false;
  const root = document.documentElement;
  return root.hasAttribute("data-netti-guide") || root.hasAttribute("data-overture");
}

/** hand a connect press to netti's rail. true when the rail took it; false means open your own sheet. */
export function connectWithNetti(ask: NettiConnectAsk): boolean {
  if (typeof window === "undefined") return false;
  if (railRetired()) return false;
  const detail: NettiConnectAsk = { ...ask, taken: false };
  window.dispatchEvent(new CustomEvent<NettiConnectAsk>(NETTI_CONNECT_EVENT, { detail }));
  return detail.taken === true;
}

/** is netti's rail here to take a connect press? (the rail marks the document while it is) */
export function nettiConnectHere(): boolean {
  return typeof document !== "undefined" && document.documentElement.hasAttribute("data-netti-connect") && !railRetired();
}
